import { useDispatch, useSelector } from 'react-redux';

import ReportDismissModal from 'dashboard/components/ReportResult/ReportDismissModal';
import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { IgnoreType, Report } from 'dashboard/models/report';
import { setInquiryReports } from 'dashboard/redux/actions/inquiry';
import { showMessage } from 'dashboard/redux/actions/messages';
import { setReport } from 'dashboard/redux/actions/report';
import { setAccountReports } from 'dashboard/redux/slices/account';
import { setCaseObjects } from 'dashboard/redux/slices/case';
import { RootState } from 'dashboard/redux/store';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import { ApiModel } from 'lib/interfaces/api';

interface IgnoreHitsModalProps {
  ignoreHitsModalOpen: boolean;
  hideIgnoreHitsModal: () => void;
  modalHeaderText?: string;
  modalDescriptionText?: string;
  report: Report;
  entityId?: string;
  ignoreType?: IgnoreType;
}

/**
 * Wraps ReportDismissModal with specific logic to make the API call to dismiss reports.
 */
const IgnoreHitsModal = ({
  report,
  entityId,
  ignoreType,
  ignoreHitsModalOpen,
  hideIgnoreHitsModal,
  modalHeaderText,
  modalDescriptionText = 'Dismissing matches will hide these results in future report runs.',
}: IgnoreHitsModalProps) => {
  const dispatch = useDispatch();
  const inquiryReports = useSelector((state: RootState) => {
    return state.inquiry.reports;
  });
  const accountReports = useSelector((state: RootState) => {
    return state.account.reports;
  });
  const caseToken = useSelector((state: RootState) => {
    return state.case.caseToken;
  });
  const caseObjects = useSelector((state: RootState) => {
    return caseToken ? state.case.case[caseToken].caseDetails?.objects : [];
  });

  // Making backend call here because this component is used in Reports/Accounts/Cases/Inquiries
  // and it doesn't make sense to pass down from a Route. In the future, may turn into a "ReportAction"
  // that gets handled in ReportResult component
  const { error: ignoreHitsError, callback: handleIgnoreHits } = useApiCallback(
    async (entityId?: string, ignoreType?: IgnoreType, ignoreReason?: string) => {
      const { data } = await SessionClientDashboard.postResourceAction<Report>(
        {
          id: report.id,
          type: report.type,
          attributes: {
            'entity-id': entityId,
            'ignore-type': ignoreType,
            'ignore-reason': ignoreReason,
          },
        },
        'ignore-hits',
      );
      // Set report for Reports
      dispatch(setReport(data));

      // Set report for Accounts
      if (accountReports) {
        dispatch(setAccountReports(accountReports.map((r) => (r.id === data.id ? data : r))));
      }
      // Set report for Inquiries
      if (inquiryReports) {
        dispatch(setInquiryReports(inquiryReports.map((r) => (r.id === data.id ? data : r))));
      }
      // Set report for Cases
      if (caseObjects && caseToken) {
        dispatch(
          setCaseObjects({
            key: caseToken,
            objects: caseObjects.map((o) => (o.id === data.id ? data : o)),
          }),
        );
      }

      dispatch(showMessage(`Successfully dismissed!`, MessageType.Success));
    },
    [report],
  );
  useApiErrorHandler({
    error: ignoreHitsError,
    onError: () =>
      dispatch(showMessage('Failed to Dismiss Matches, please try again.', MessageType.Failure)),
    onNetworkError: () =>
      dispatch(showMessage('Failed to Dismiss Matches, please try again.', MessageType.Failure)),
  });

  return (
    <ReportDismissModal
      isOpen={ignoreHitsModalOpen}
      onClose={hideIgnoreHitsModal}
      onSubmit={(reason) => handleIgnoreHits(entityId, ignoreType, reason)}
      header={modalHeaderText || getModalHeaderText(report.type)}
      description={modalDescriptionText}
    />
  );
};

const getModalHeaderText = (reportType: ApiModel) => {
  switch (reportType) {
    case ApiModel.ReportAdverseMedia:
      return 'Dismiss Adverse Media Matches?';
    case ApiModel.ReportBusinessAdverseMedia:
      return 'Dismiss Business Adverse Media Matches?';
    case ApiModel.ReportBusinessWatchlist:
      return 'Dismiss Business Watchlist Matches?';
    case ApiModel.ReportWatchlist:
      return 'Dismiss Watchlist Matches?';
    case ApiModel.ReportPoliticallyExposedPerson:
      return 'Dismiss PEP Matches?';
    default:
      'Dismiss Matches?';
  }
};

export default IgnoreHitsModal;
